import { splitRecipients } from '@/utils/internalEmailRecipients';
import type { EmailUser } from '@/components/shared/email/emailValue';
import { toAttachmentPayload } from './attachDocumentSelection';
import type { AttachmentSelection } from './attachDocumentSelection';
import type {
  InternalEmail,
  InternalEmailUser,
  InternalEmailPrefill,
  InternalEmailRecipientSeed,
  SendInternalEmailPayload,
} from '@/api/internalEmails';

/** The Formik slice the send slider holds. */
export interface SendInternalEmailValues {
  from?: string | number | null;
  fromUserObject?: { id?: string | number; isSharedEmail?: boolean } | null;
  to?: (string | number)[];
  cc?: (string | number)[];
  bcc?: (string | number)[];
  subject?: string;
  body?: string;
}

/**
 * The From row: a shared mailbox if the send is configured for one, otherwise the
 * user it is configured for, otherwise whoever is composing.
 *
 * The whole user, not just the id — the From menu only offers users with a
 * connected mailbox, so a current user who has not connected one yet would have
 * no option to take a label from and the row would read as empty.
 */
const fromRow = (
  source: InternalEmailPrefill | InternalEmail | undefined,
  currentUser?: EmailUser
) => {
  const sender = source?.ats_shared_email?.id
    ? { ...source.ats_shared_email, isSharedEmail: true }
    : (source?.sender ?? currentUser);

  return { from: sender?.id, fromUserObject: sender ?? null };
};

/** Rebuilds one recipient row from the prefill's three parallel fields. */
const seedRow = (seed?: InternalEmailRecipientSeed): (string | number)[] => [
  ...(seed?.users ?? []).map((user) => String(user.id)),
  ...(seed?.participant_groups ?? []),
  ...(seed?.org_roles ?? []).map((role) => `org_role:${role.id}`),
];

/**
 * Seeds the slider from the activity's configuration.
 *
 * Roles arrive as the tokens they were configured as, alongside the people they
 * currently resolve to — the sender can drop either before sending.
 */
export const toSendInternalEmailValues = (
  prefill: InternalEmailPrefill | undefined,
  currentUser?: EmailUser
): SendInternalEmailValues => ({
  ...fromRow(prefill, currentUser),
  to: seedRow(prefill?.to),
  cc: seedRow(prefill?.cc),
  bcc: seedRow(prefill?.bcc),
  subject: prefill?.subject ?? '',
  body: prefill?.body ?? '',
});

/**
 * One recipient row of an email already queued: what it was composed from when the
 * API reports that — the people picked and the roles as tokens, the same shape the
 * prefill seeds — so a role chip comes back as the chip. A copy queued before the
 * API recorded its composition reports only the people the roles resolved to; those
 * seed the row instead, and the email stops tracking the role.
 */
const editRow = (
  composed: InternalEmailRecipientSeed | undefined,
  resolved: InternalEmailUser[] | undefined
): (string | number)[] =>
  composed
    ? seedRow(composed)
    : (resolved ?? []).map((user) => String(user.id));

/** Seeds the composer from an email already queued, for an edit. */
export const toEditInternalEmailValues = (
  email: InternalEmail | undefined,
  currentUser?: EmailUser
): SendInternalEmailValues => ({
  ...fromRow(email, currentUser),
  to: editRow(email?.recipients?.to, email?.to),
  cc: editRow(email?.recipients?.cc, email?.cc),
  bcc: editRow(email?.recipients?.bcc, email?.bcc),
  subject: email?.subject ?? '',
  body: email?.body ?? '',
});

/** Adds the reply prefix unless the subject already carries one. */
const replySubject = (subject?: string) => {
  const trimmed = (subject ?? '').trim();
  if (!trimmed) return 'Re:';
  return /^re:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
};

/** Everyone still on the thread, in order, without the person replying. */
const withoutSelf = (
  users: (InternalEmailUser | undefined)[],
  currentUserId?: string | number,
  taken = new Set<string>()
) =>
  users.reduce<string[]>((ids, user) => {
    const id = user?.id === undefined ? '' : String(user.id);
    if (!id || id === String(currentUserId) || taken.has(id)) return ids;
    taken.add(id);
    ids.push(id);
    return ids;
  }, []);

/**
 * Seeds the composer for a reply to an internal email.
 *
 * Reply-all shaped, because an internal email is a discussion among teammates:
 * the sender and the original To recipients are addressed, the original Cc stays
 * copied, and the person replying is dropped from both. Bcc is not carried — a
 * reply must not disclose who was blind-copied.
 *
 * The From is seeded the way a fresh ad-hoc send seeds it — whoever is composing,
 * or the shared mailbox this candidate's email goes out from — not the sender of
 * the email being answered. The reply is the replier's.
 */
export const toReplyInternalEmailValues = (
  parent: InternalEmail | undefined,
  prefill: InternalEmailPrefill | undefined,
  currentUser?: EmailUser
): SendInternalEmailValues => {
  const taken = new Set<string>();

  return {
    ...fromRow(prefill, currentUser),
    to: withoutSelf(
      [parent?.sender, ...(parent?.to ?? [])],
      currentUser?.id,
      taken
    ),
    cc: withoutSelf(parent?.cc ?? [], currentUser?.id, taken),
    bcc: [],
    subject: replySubject(parent?.subject),
    body: '',
  };
};

interface BuildPayloadArgs {
  values: SendInternalEmailValues;
  applicationId: number | string;
  activityId?: number | string;
  attachments: AttachmentSelection;
  /** Send time, owned by the footer rather than the form. */
  scheduleAt?: string | null;
  timezone?: string | null;
  /** The internal email being replied to, when this send is a reply. */
  replyToInternalEmailId?: number | string;
}

/**
 * Maps the form onto the send request.
 *
 * Each recipient row is split back into users, job roles and Kula roles; the
 * roles are only resolved to people server-side, at send time.
 */
export const toSendInternalEmailPayload = ({
  values,
  applicationId,
  activityId,
  attachments,
  scheduleAt = null,
  timezone,
  replyToInternalEmailId,
}: BuildPayloadArgs): SendInternalEmailPayload => {
  const isSharedEmail = Boolean(values.fromUserObject?.isSharedEmail);
  const payload: SendInternalEmailPayload = {
    ats_application_id: applicationId,
    ...(activityId ? { ats_job_stage_action_id: activityId } : {}),
    sender_id: isSharedEmail ? null : (values.from ?? null),
    ats_shared_email_id: isSharedEmail
      ? (values.fromUserObject?.id ?? null)
      : null,
    subject: values.subject,
    body: values.body,
    scheduled_at: scheduleAt,
    timezone: scheduleAt ? (timezone ?? null) : null,
    ...(replyToInternalEmailId
      ? { reply_to_ats_user_message_id: replyToInternalEmailId }
      : {}),
    ...toAttachmentPayload(attachments),
  };

  (['to', 'cc', 'bcc'] as const).forEach((prefix) => {
    const { userIds, participantGroups, orgRoleIds } = splitRecipients(
      values[prefix]
    );
    payload[`${prefix}_user_ids`] = userIds;
    payload[`${prefix}_participant_groups`] = participantGroups;
    payload[`${prefix}_org_role_ids`] = orgRoleIds;
  });

  return payload;
};
